import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

export default function Employees() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setItems((await api.get("/employees")).data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Mitarbeiter löschen?")) return;
    try { await api.delete(`/employees/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="crm-employees">
      <div className="mb-5 md:mb-8 flex items-start justify-between gap-3 md:gap-4 flex-wrap">
        <div>
          <div className="hidden md:block text-xs font-medium text-[#6B7F3E] uppercase tracking-wider mb-2">CRM</div>
          <h1 className="hidden md:block font-display text-3xl font-semibold text-[#1A201A] tracking-tight">Mitarbeiter</h1>
          <p className="text-sm text-[#5C6B5D] mt-1">Team-Übersicht für Touren-Zuweisung und Kontakt.</p>
        </div>
        <Button className="bg-[#6B7F3E] hover:bg-[#5A6B34]" onClick={() => setCreating(true)} data-testid="employee-new-btn">
          <Plus className="w-4 h-4 mr-1" /> Neu
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading && <div className="text-sm text-[#5C6B5D] col-span-full">Lädt…</div>}
        {!loading && items.length === 0 && (
          <div className="text-sm text-[#5C6B5D] col-span-full bg-white border border-[#E5E7EB] rounded-xl p-8 text-center">
            Noch keine Mitarbeiter angelegt.
          </div>
        )}
        {items.map((e) => (
          <div
            key={e.id} data-testid={`employee-card-${e.id}`}
            className="bg-white border border-[#E5E7EB] rounded-xl p-5 hover:border-[#6B7F3E]/50"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 rounded-full bg-[#E8ECE1] flex items-center justify-center text-[#2C3E2D] font-semibold">
                {e.name.split(" ").map(x => x[0]).slice(0, 2).join("").toUpperCase()}
              </div>
              <button onClick={() => remove(e.id)} data-testid={`employee-delete-${e.id}`} className="text-[#B85C38] hover:text-[#7A2E14]">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div className="font-medium text-[#1A201A]">{e.name}</div>
            <div className="text-xs text-[#6B7F3E] uppercase tracking-wider">{e.role}</div>
            <div className="text-xs text-[#5C6B5D] mt-2 space-y-0.5">
              {e.email && <div>{e.email}</div>}
              {e.phone && <div>{e.phone}</div>}
            </div>
            {e.notes && <div className="text-xs text-[#4A5D4E] mt-3 border-t border-[#E5E7EB] pt-3">{e.notes}</div>}
          </div>
        ))}
      </div>

      <EmpDialog open={creating} onOpenChange={setCreating} onSaved={load} />
    </div>
  );
}

function EmpDialog({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "Mitarbeiter", active: true, notes: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm({ name: "", email: "", phone: "", role: "Mitarbeiter", active: true, notes: "" }); }, [open]);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name) { toast.error("Name ist Pflicht"); return; }
    setSaving(true);
    try {
      await api.post("/employees", { ...form, email: form.email || null, phone: form.phone || null });
      toast.success("Mitarbeiter angelegt");
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-white">
        <DialogHeader>
          <DialogTitle className="font-display">Neuer Mitarbeiter</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Name *</Label><Input className="mt-1.5" value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="employee-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>E-Mail</Label><Input className="mt-1.5" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="employee-email-input" /></div>
            <div><Label>Telefon</Label><Input className="mt-1.5" value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="employee-phone-input" /></div>
          </div>
          <div><Label>Rolle</Label><Input className="mt-1.5" value={form.role} onChange={(e) => update("role", e.target.value)} data-testid="employee-role-input" /></div>
          <div className="flex items-center justify-between">
            <Label>Aktiv</Label>
            <Switch checked={form.active} onCheckedChange={(v) => update("active", v)} data-testid="employee-active-switch" />
          </div>
          <div><Label>Notizen</Label><Textarea className="mt-1.5" rows={2} value={form.notes} onChange={(e) => update("notes", e.target.value)} data-testid="employee-notes-input" /></div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button className="bg-[#6B7F3E] hover:bg-[#5A6B34]" onClick={save} disabled={saving} data-testid="employee-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Speichern"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
